package floris.graphics.backoffice.pages.admin.contributors.manage

import floris.graphics.extensions.isValidUrl
import floris.graphics.models.domain.models.Contributor
import floris.graphics.models.dto.requests.contributor.InsertOrUpdateContributor
import floris.graphics.repo.ContributorRepo
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import org.w3c.dom.url.URL
import org.w3c.files.File

class ManageContributorViewModel(
    private val contributorId: String?,
    private val contributorRepo: ContributorRepo = ContributorRepo,
    private val coroutineScope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
) {
    private val _state = MutableStateFlow(ManageContributorState())
    val state = _state.asStateFlow()

    private val _eventFlow = Channel<ManageContributorUiEvent>()
    val eventFlow = _eventFlow.receiveAsFlow()

    fun onEvent(event: ManageContributorEvent) = coroutineScope.launch {
        when (event) {
            is ManageContributorEvent.LoadManageContributor -> getContributor()
            is ManageContributorEvent.Delete -> deleteContributor()
            is ManageContributorEvent.Cancel -> _eventFlow.send(ManageContributorUiEvent.NavigateBack)
            is ManageContributorEvent.Save -> saveContributor()

            is ManageContributorEvent.UpdateName -> {
                val name = event.name
                val insertOrUpdateContributor = state.value.insertOrUpdateContributor.copy(name = name)
                _state.update { it.copy(insertOrUpdateContributor = insertOrUpdateContributor) }
            }

            is ManageContributorEvent.UpdateBio -> {
                val bio = event.bio
                val insertOrUpdateContributor = state.value.insertOrUpdateContributor.copy(bio = bio)
                _state.update { it.copy(insertOrUpdateContributor = insertOrUpdateContributor) }
            }

            is ManageContributorEvent.UpdateWebsite -> {
                val website = event.website
                val insertOrUpdateContributor = state.value.insertOrUpdateContributor.copy(website = website.ifEmpty { null })
                _state.update { it.copy(insertOrUpdateContributor = insertOrUpdateContributor) }
            }

            is ManageContributorEvent.UpdateLogo -> {
                if (contributorId.isNullOrBlank()) {
                    // If we are creating a new contributor, we can just update the state with the logo
                    _state.update {
                        it.copy(
                            logoFile = event.logo,
                            logoUrl = event.logo.let { URL.createObjectURL(it) }
                        )
                    }
                } else {
                    // If we are updating an existing contributor, we need to upload the image remotely
                    updateImageRemotely(event.logo)
                }
            }

            is ManageContributorEvent.RemoveLogo -> {
                if (contributorId.isNullOrBlank()) {
                    // If we are creating a new contributor, we can just remove the logo from the state
                    _state.update { it.copy(logoFile = null, logoUrl = null) }
                } else {
                    // If we are updating an existing contributor, we need to remove the logo remotely
                    updateImageRemotely(removeLogo = true)
                }
            }
        }
    }

    init {
        onEvent(ManageContributorEvent.LoadManageContributor)
    }

    private suspend fun getContributor() {
        if (contributorId.isNullOrBlank()) return

        val contributor = contributorRepo.getContributor(contributorId) ?: Contributor()
        val insertOrUpdateContributor = InsertOrUpdateContributor(
            name = contributor.name,
            bio = contributor.bio,
            website = contributor.website?.toString()
        )
        _state.update { it.copy(insertOrUpdateContributor = insertOrUpdateContributor, originalContributor = contributor) }
    }

    private suspend fun saveContributor() {
        val insertOrUpdateContributor = state.value.insertOrUpdateContributor

        if (contributorId.isNullOrBlank()) {
            contributorRepo.insertContributor(insertOrUpdateContributor, state.value.logoFile)
        } else {
            contributorRepo.updateContributor(contributorId, insertOrUpdateContributor)
        }

        _eventFlow.send(ManageContributorUiEvent.NavigateBack)
    }

    private suspend fun updateImageRemotely(logo: File? = null, removeLogo: Boolean = false) {
        if (contributorId.isNullOrBlank()) return

        _state.update { it.copy(uploadingLogo = true) }
        val updatedContributor = contributorRepo.updateContributorImage(contributorId, logo, removeLogo)
        if (updatedContributor != null) {
            _state.update { it.copy(originalContributor = updatedContributor, logoFile = null, uploadingLogo = false) }
        } else {
            _state.update { it.copy(uploadingLogo = false) }
            _eventFlow.send(ManageContributorUiEvent.ShowError)
        }
    }

    private suspend fun deleteContributor() {
        if (contributorId.isNullOrBlank()) return

        contributorRepo.deleteContributor(contributorId)
        _eventFlow.send(ManageContributorUiEvent.NavigateBack)
    }
}

data class ManageContributorState(
    val originalContributor: Contributor = Contributor(),
    val logoFile: File? = null,
    val logoUrl: String? = null,
    val uploadingLogo: Boolean = false,
    val insertOrUpdateContributor: InsertOrUpdateContributor = InsertOrUpdateContributor(),
    val errorText: String = ""
) {
    private val nameChanged = insertOrUpdateContributor.name != originalContributor.name
    private val bioChanged = insertOrUpdateContributor.bio != originalContributor.bio
    private val websiteChanged = insertOrUpdateContributor.website != originalContributor.website?.toString()
    private val logoChanged = logoFile != null

    val isValidWebsite: Boolean
        get() = insertOrUpdateContributor.website.isNullOrEmpty() || (insertOrUpdateContributor.website?.isValidUrl() ?: false)

    val hasChanged: Boolean
        get() = nameChanged || bioChanged || websiteChanged || logoChanged

    val isCreationFlow: Boolean
        get() = originalContributor.id.isBlank()
}

sealed class ManageContributorEvent {
    data object LoadManageContributor : ManageContributorEvent()
    data class UpdateName(val name: String) : ManageContributorEvent()
    data class UpdateBio(val bio: String) : ManageContributorEvent()
    data class UpdateWebsite(val website: String) : ManageContributorEvent()
    data class UpdateLogo(val logo: File) : ManageContributorEvent()
    data object RemoveLogo : ManageContributorEvent()
    data object Save : ManageContributorEvent()
    data object Cancel : ManageContributorEvent()
    data object Delete : ManageContributorEvent()
}

sealed class ManageContributorUiEvent {
    data object ShowError : ManageContributorUiEvent()
    data object NavigateBack : ManageContributorUiEvent()
}
